/**
 * @fileoverview Student sign-in form — email + password.
 *
 * Renders as the Student tab INSIDE AuthPortalLayout, which owns the brand
 * mark, the portal tab strip, the heading and the "Back to home" link.
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/api";
import { AuthCard, AuthField, AuthSubmit, AuthNote, authLinkCls } from "@/components/auth/authUi";

const StudentLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Inline field-level validation errors, surfaced on submit (server errors
  // still go through the toast).
  const [errors, setErrors] = useState({});
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!EMAIL_RE.test(email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }
    if (!password) {
      nextErrors.password = "Password is required";
    } else if (password.length < 6) {
      nextErrors.password = "Password must be at least 6 characters";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const res = await axios.post(
        `${API}/student/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      const fullName = `${res.data.account.first_name} ${res.data.account.last_name}`.trim();
      toast.success(`Welcome back, ${fullName}!`);
      navigate("/student/dashboard", { state: { student: res.data.account } });
    } catch (err) {
      if (!mountedRef.current) return;
      toast.error(getErrorMessage(err, "Invalid email or password"));
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <AuthCard>
          <AuthField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: undefined })); }}
            required
            placeholder="your@email.com"
            autoComplete="email"
            error={errors.email}
            errorTestId="email-error"
          />
          <AuthField
            label="Password"
            type="password"
            reveal
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((p) => ({ ...p, password: undefined })); }}
            required
            minLength={6}
            placeholder="Your password"
            autoComplete="current-password"
            error={errors.password}
            errorTestId="password-error"
          >
            <div className="mt-2 text-right">
              <Link to="/student/forgot-password" className={`text-xs ${authLinkCls}`}>
                Forgot password?
              </Link>
            </div>
          </AuthField>
        </AuthCard>

        <AuthSubmit submitting={submitting} icon={GraduationCap}>
          {submitting ? "Signing In..." : "Sign In"}
        </AuthSubmit>
      </form>

      <div className="mt-6 text-center">
        <AuthNote>Don't have an account? Ask your teacher for an invite link.</AuthNote>
      </div>
    </div>
  );
};

export default StudentLoginPage;
